import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

interface OneOfFieldWatcherProps {
  fields: string[];
}

/**
 * Watches for changes in a set of Formik fields. When one of the provided fields changes, the other fields are set to null.
 */
const OneOfFieldWatcher = ({ fields }: OneOfFieldWatcherProps) => {
  const { values, setFieldValue } = useFormikContext<any>();

  // Keep track of previous values to detect changes
  const prevValuesRef = useRef<any>({});

  useEffect(() => {
    const prevValues = prevValuesRef.current;

    for (const field of fields) {
      const prev = prevValues[field];
      const current = values[field];

      const changed = prev !== current && !(prev === null && current === null); // detects any real change

      if (changed && current !== null) {
        // This field was updated; reset the others
        for (const other of fields) {
          if (other !== field && values[other] !== null) {
            setFieldValue(other, null);
          }
        }
        break; // only respond to one change at a time
      }
    }

    prevValuesRef.current = { ...values };
  }, [values, fields, setFieldValue]);

  return null; // This component doesn't render anything
};

export default OneOfFieldWatcher;
